import * as THREE from 'three'
import { PlayerMesh } from './creatures'

// Render the NEW 3D hero mesh (per character colour) to a data-URL so the DOM menus (character select, recap) show
// the actual 3D character instead of the old pixel-art sprite. A tiny offscreen WebGL context, rendered once per
// colour and cached. Everything is guarded: on any failure it returns '' and the caller falls back to the 2D sprite.

let renderer: THREE.WebGLRenderer | null = null
let scene: THREE.Scene
let camera: THREE.PerspectiveCamera
let hero: PlayerMesh
let failed = false
const cache = new Map<number, string>()

function init(): boolean {
  if (renderer) return true
  if (failed) return false
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true })
    renderer.setSize(160, 160)
    renderer.setPixelRatio(2)
    renderer.outputColorSpace = THREE.SRGBColorSpace
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    renderer.toneMappingExposure = 1.12
    scene = new THREE.Scene()
    // same warm cozy rig as the game, so menu portraits match in-world lighting
    scene.add(new THREE.HemisphereLight(0x9fd8ff, 0xb9966b, 0.75))
    scene.add(new THREE.AmbientLight(0xfff2dc, 0.42))
    const sun = new THREE.DirectionalLight(0xffd9a0, 1.05)
    sun.position.set(2.5, 4, 3)
    scene.add(sun)
    hero = new PlayerMesh()
    scene.add(hero.group)
    camera = new THREE.PerspectiveCamera(34, 1, 0.1, 50)
    camera.position.set(2.4, 1.55, 1.25) // in front + above, looking at the face
    camera.lookAt(0, 0.92, 0)
    return true
  } catch {
    failed = true
    renderer = null
    return false
  }
}

/** Data-URL of a 160px 3D portrait for a character colour (cached). '' if WebGL is unavailable → caller falls back. */
export function characterIconURL(colorHex: number): string {
  const hit = cache.get(colorHex)
  if (hit !== undefined) return hit
  if (!init()) { cache.set(colorHex, ''); return '' }
  try {
    hero.update(0, 0, 0, 0, colorHex, 1) // facing 0 → the +x face points toward the camera
    renderer!.render(scene, camera)
    const url = renderer!.domElement.toDataURL()
    cache.set(colorHex, url)
    return url
  } catch {
    cache.set(colorHex, '')
    return ''
  }
}

/** An <img> tag for the 3D portrait, or '' if unavailable (caller can fall back to the sprite icon). */
export function characterIconHTML(colorHex: number, size = 56): string {
  const url = characterIconURL(colorHex)
  if (!url) return ''
  return `<img class="px-ico" width="${size}" height="${size}" src="${url}" alt="" draggable="false">`
}
